import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
} from "@angular/core"
import { DesignService } from "@services/design.service"
import {
    MobileContextSheetOverlayHandle,
    MobileContextSheetOverlayService,
} from "../mobile-context-sheet/mobile-context-sheet-overlay.service"
import { Subscription } from "rxjs"

export interface MenuOptions<T> {
    options: MenuOption<T>[]
    onClose?: () => void
    title?: string
    coordinates: {
        x: number
        y: number
    }
}

export interface MenuOption<T> {
    data: T | MenuOption<T>[]
    icon: string | undefined
    iconLoading?: boolean
    iconWidth?: string
    iconHeight?: string
    text: string
    loading: boolean
    buttonClass?: "delete" | "rename" | "primary" | "secondary"
    subtitle?: string | undefined
    avData?: string
    enabled: boolean

    /**
     * This function is called directly when the user clicks on the menu option.
     */
    onClick: { (arg: T, event: MouseEvent): any }

    /**
     * This function is called when the user confirms the action.
     * For example, when the user clicks on the "Rename" option, the "Rename" option
     * will be replaced with a text input. When the user clicks on the "Confirm" button,
     * this function will be called.
     */
    onConfirm?: { (arg: T): Promise<void> }
}

@Component({
    selector: "menu-options",
    templateUrl: "menu-options.component.html",
    styleUrls: ["menu-options.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuOptionsComponent implements OnDestroy {
    @Output() close: EventEmitter<any> = new EventEmitter()
    @Input() menuOptions: MenuOptions<any>
    @Input() width: number | undefined
    @Input() title: string | undefined
    @ViewChild("menuOptionsContainer")
    menuOptionsContainer: ElementRef<HTMLElement>
    @ViewChild("menuOptionsWrapper") menuOptionsWrapper: ElementRef<HTMLElement>
    @ViewChild("mobileSheetTemplate")
    mobileSheetTemplate: TemplateRef<unknown>

    public readonly isMobileSheet =
        window.matchMedia("(max-width: 767px)").matches
    public mobileSheetOpen = false
    public mobileSheetClosing = false

    public hoveredOption:
        | { option: MenuOption<any>; element: HTMLDivElement }
        | undefined

    private readonly padding: number = 18
    private mobileOverlay: MobileContextSheetOverlayHandle | undefined
    private backdropSubscription: Subscription | undefined
    private closeTimer: ReturnType<typeof setTimeout> | undefined
    private openFrame: number | undefined
    private closeAfterAction = false

    constructor(
        private ref: ChangeDetectorRef,
        private designService: DesignService,
        private mobileSheetOverlay: MobileContextSheetOverlayService,
        private viewContainerRef: ViewContainerRef
    ) {}

    ngOnInit() {}

    @HostListener("document:mousedown", ["$event"])
    onGlobalClick($event): void {
        if (this.isMobileSheet) {
            return
        }

        if (!this.menuOptionsWrapper.nativeElement.contains($event.target)) {
            this.requestClose()
        }
    }

    ngAfterViewInit() {
        if (this.isMobileSheet) {
            this.mobileOverlay = this.mobileSheetOverlay.open(
                this.mobileSheetTemplate,
                this.viewContainerRef
            )
            this.backdropSubscription = this.mobileOverlay.overlayRef
                .backdropClick()
                .subscribe(() => this.requestClose())
            this.openFrame = requestAnimationFrame(() => {
                this.mobileSheetOpen = true
                this.ref.detectChanges()
            })
            return
        }

        this.menuOptions.coordinates.x = this.formatX(
            this.menuOptions.coordinates.x
        )

        this.menuOptions.coordinates.y = this.formatY(
            this.menuOptions.coordinates.y
        )

        this.ref.detectChanges()
    }

    ngOnDestroy(): void {
        if (this.openFrame !== undefined) {
            cancelAnimationFrame(this.openFrame)
        }
        if (this.closeTimer !== undefined) {
            clearTimeout(this.closeTimer)
        }
        this.backdropSubscription?.unsubscribe()
        this.mobileOverlay?.dispose()
    }

    public getWidth(): number {
        if (this.isMobileSheet) {
            return window.innerWidth
        }

        return (
            this.menuOptionsContainer.nativeElement.getBoundingClientRect()
                .width - this.padding
        )
    }

    /**
     * This function is used in order to reposition the y coordinate value in case
     * the menu options are too close to the bottom of the screen.
     * @param y
     */
    private formatY(y: number): number {
        const playerBarHeight = parseInt(
            this.designService
                .getCSSProperty("player-bar-height")
                .replace("px", "")
        )

        const endY =
            y +
            this.menuOptionsContainer.nativeElement.getBoundingClientRect()
                .height +
            playerBarHeight

        if (window.innerHeight < endY) {
            y -= endY - window.innerHeight
        }

        return Math.max(8, y)
    }

    /**
     * This function is used in order to reposition the x coordinate value in case
     * the menu options are too close to the side of the screen.
     */
    private formatX(x: number): number {
        const endX =
            x +
            this.menuOptionsContainer.nativeElement.getBoundingClientRect()
                .width

        if (window.innerWidth < endX) {
            x -= endX - window.innerWidth
        }

        return Math.max(8, x)
    }

    public createMenuOptions() {
        const firstX =
            this.menuOptions.coordinates.x +
            this.hoveredOption.element.getBoundingClientRect().width +
            this.padding

        let secondX = this.formatX(firstX)

        if (firstX !== secondX) {
            secondX =
                this.menuOptions.coordinates.x -
                this.hoveredOption.element.getBoundingClientRect().width -
                this.padding
        }

        return {
            options: this.hoveredOption.option.data,
            coordinates: {
                x: secondX,
                y: this.hoveredOption.element.getBoundingClientRect().top,
            },
        } as MenuOptions<any>
    }

    public hoverOption(event: MouseEvent, option: MenuOption<any>) {
        event.stopPropagation()

        this.hoveredOption = {
            option: option,
            element: event.target as HTMLDivElement,
        }
    }

    public async clickOnOption(option: MenuOption<any>, event: MouseEvent) {
        if (this.isMenuOption(option.data)) {
            this.hoveredOption = {
                option,
                element: event.currentTarget as HTMLDivElement,
            }
            return
        }

        if (!option.enabled) {
            return
        }

        option.loading = true

        await option.onClick(option.data, event)
        option.loading = false
        this.requestClose(true)
    }

    public closeSubmenu(event?: { actionCompleted?: boolean }): void {
        if (event?.actionCompleted) {
            this.requestClose(true)
            return
        }

        this.hoveredOption = undefined
        this.ref.detectChanges()
    }

    public requestClose(actionCompleted = false): void {
        this.closeAfterAction ||= actionCompleted

        if (this.mobileSheetClosing) {
            return
        }

        if (!this.isMobileSheet) {
            this.finishClose()
            return
        }

        this.mobileSheetClosing = true
        this.mobileSheetOpen = false
        this.mobileOverlay?.overlayRef.backdropElement?.classList.add(
            "mobile-context-sheet-backdrop-closing"
        )
        this.ref.detectChanges()

        const reducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches
        this.closeTimer = setTimeout(
            () => this.finishClose(),
            reducedMotion ? 1 : 220
        )
    }

    private finishClose(): void {
        this.backdropSubscription?.unsubscribe()
        this.mobileOverlay?.dispose()
        this.mobileOverlay = undefined

        if (this.menuOptions.onClose) {
            this.menuOptions.onClose()
            return
        }

        this.close.emit({ actionCompleted: this.closeAfterAction })
    }

    public isMenuOption(option: any | MenuOption<any>[]) {
        return (
            Array.isArray(option) &&
            option.length > 0 &&
            typeof option[0] === "object" &&
            option[0].text !== undefined
        )
    }
}
